import React, { useState, useRef, useEffect } from "react";
import { ArrowUp, Bot, MoreVertical, RefreshCw, AlertCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { locales } from "./locales";
import { chatService } from "./api/chatService";

// Generate unique session ID
const generateSessionId = () => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

// Get URL parameters
const getURLParams = () => {
  const params = new URLSearchParams(window.location.search);
  const langParam = params.get('lang') || 'tr';
  // Validate language parameter - fallback to English if invalid
  const validLanguages = ['tr', 'en'];
  const lang = validLanguages.includes(langParam) ? langParam : 'en';

  return {
    lang: lang,
    encodedBranch: params.get('b') || null  // Encoded branchId
  };
};

export default function ChatInterface() {
  const urlParams = getURLParams();

  const language = urlParams.lang;
  const t = locales[language];

  const [messages, setMessages] = useState([
    {
      title: t['welcomeTitle'],
      subtitle: t['welcomeSubtitle'],
      accentSymbol: "✦",
      text: `${t['welcomeTitle']}\n${t['welcomeSubtitle']}`,
      sender: "bot",
    },
  ]);
  const [input, setInput] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sessionId, setSessionId] = useState(generateSessionId());
  const messagesEndRef = useRef(null);
  const currentSessionRef = useRef(sessionId);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  const handleSubmit = async (message) => {
    if (!message.trim()) return;

    // Capture current session ID for this request
    const requestSessionId = sessionId;

    setMessages((prev) => [...prev, { text: message, sender: "user" }]);
    setInput("");

    const loadingMessageId = Date.now();
    setMessages((prev) => [
      ...prev,
      {
        id: loadingMessageId,
        text: t['thinkingMessage'],
        sender: "bot",
        isLoading: true,
      },
    ]);

    try {
      const response = await chatService.sendMessage(message, requestSessionId, language, urlParams.encodedBranch);

      // Only update messages if session hasn't changed (user hasn't clicked clear)
      if (currentSessionRef.current === requestSessionId) {
        setMessages((prev) => prev.filter((msg) => msg.id !== loadingMessageId));

        // Check if message limit is reached
        const botMessage = response.message_limit_reached
          ? t['messageLimitReached']
          : response.response;

        setMessages((prev) => [...prev, { text: botMessage, sender: "bot" }]);
      }
    } catch (error) {
      console.error("Error details:", error);

      // Only show error if session hasn't changed
      if (currentSessionRef.current === requestSessionId) {
        setMessages((prev) => prev.filter((msg) => msg.id !== loadingMessageId));
        setMessages((prev) => [
          ...prev,
          {
            text: t['errorMessage'],
            sender: "bot",
            isError: true,
          },
        ]);

        // Detaylı hata sadece console'da görünsün
        console.error("API Error:", error.message);
      }
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await chatService.clearHistory(sessionId);

      // Reset messages with welcome message and create new session
      setMessages([
        {
          title: t['welcomeTitle'],
          subtitle: t['welcomeSubtitle'],
          accentSymbol: "✦",
          text: `${t['welcomeTitle']}\n${t['welcomeSubtitle']}`,
          sender: "bot",
        },
      ]);
      const newSessionId = generateSessionId();
      setSessionId(newSessionId);
      currentSessionRef.current = newSessionId;
    } catch (error) {
      console.error("Error clearing chat history:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="chat-bg flex items-center justify-center min-h-screen p-4 transition-all duration-500">
      <div className="chat-phone w-full max-w-lg overflow-hidden">
        <div className="flex flex-col h-[90vh]">
          {/* Header Section */}
          <div className="chat-header text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="avatar">
                <Bot size={22} />
              </div>
              <div>
                <div className="font-semibold text-base">
                  Chef AI <span className="text-xs font-normal opacity-75">by Pardon AI</span>
                </div>
                <div className="flex items-center space-x-1 text-sm text-white/80">
                  <span className="status-dot" />
                  <span>Online</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                className="icon-btn"
                title="Sohbeti temizle"
                disabled={isRefreshing}
              >
                <RefreshCw
                  size={18}
                  className={isRefreshing ? "animate-spin" : ""}
                />
              </button>
              <div className="menu-btn">
                <MoreVertical size={18} />
              </div>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 chat-messages">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${
                  message.sender === "user" ? "justify-end" : "justify-start"
                } message-enter`}
              >
                <div className={`flex flex-col space-y-1 ${
                  message.sender === "user" ? "items-end" : "items-start"
                }`}>
                  {message.sender === "bot" && (
                    <div className="bot-label">Pardon AI</div>
                  )}
                  <div
                    className={`message-bubble ${
                      message.sender === "user" ? "user-bubble" : "bot-bubble"
                    }`}
                  >
                    {message.accentSymbol ? (
                      <div className="space-y-1.5">
                        <div>
                          {message.title}{" "}
                          <span className="bot-accent">
                            {message.accentSymbol}
                          </span>
                        </div>
                        <div>{message.subtitle}</div>
                      </div>
                    ) : message.sender === "bot" ? (
                      <div className="markdown-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.text}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div>{message.text}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Disclaimer Banner */}
          <div className="disclaimer-banner">
            <AlertCircle size={14} className="disclaimer-icon" />
            <span className="disclaimer-text">
              {t['disclaimerText']}
            </span>
          </div>

          {/* Input Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(input);
            }}
            className="chat-input-shell"
          >
            <div className="chat-input-inner">
              <input
                type="text"
                name="message"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSubmit(input);
                  }
                }}
                placeholder={t['inputPlaceholder']}
                className="chat-input-field"
              />
              <button
                type="submit"
                className="chat-input-send"
                aria-label="Gönder"
              >
                <ArrowUp size={20} className="text-foreground" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
